import { apiErrorBodySchema } from '@contracts/common'

export type ApiErrorKind = 'network' | 'timeout' | 'http' | 'validation' | 'aborted'

interface ApiErrorInit {
  kind: ApiErrorKind
  message: string
  method: string
  url: string
  status?: number
  code?: string
  detail?: unknown
  issues?: string[]
  cause?: unknown
}

/** The single error type features see from the API layer. */
export class ApiError extends Error {
  override readonly name = 'ApiError'
  readonly kind: ApiErrorKind
  readonly method: string
  readonly url: string
  readonly status: number | undefined
  /** Stable backend error code (e.g. `invalid_credentials`) when provided. Branch on this, not on `message`. */
  readonly code: string | undefined
  /** Backend error detail (FastAPI `detail`) when provided. */
  readonly detail: unknown
  /** Contract violations for `validation` errors. */
  readonly issues: string[]

  constructor(init: ApiErrorInit) {
    super(init.message, { cause: init.cause })
    this.kind = init.kind
    this.method = init.method
    this.url = init.url
    this.status = init.status
    this.code = init.code
    this.detail = init.detail
    this.issues = init.issues ?? []
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

/** Extracts the readable message and the stable code from a backend error body. */
export function describeErrorBody(body: unknown): {
  detail: unknown
  message: string | undefined
  code?: string
} {
  const parsed = apiErrorBodySchema.safeParse(body)
  if (!parsed.success) return { detail: body, message: undefined }
  if ('code' in parsed.data) {
    return { detail: body, message: parsed.data.message, code: parsed.data.code }
  }
  const { detail } = parsed.data
  const message =
    typeof detail === 'string'
      ? detail
      : detail.map((item) => `${item.loc.join('.')}: ${item.msg}`).join('; ')
  return { detail, message }
}

/** Transient failures worth retrying: network, timeout and 5xx. Never 4xx or contract violations. */
export function isRetryableError(error: unknown): boolean {
  if (!isApiError(error)) return false
  if (error.kind === 'network' || error.kind === 'timeout') return true
  return error.kind === 'http' && error.status !== undefined && error.status >= 500
}
